/*
 * Omnitub — "Will it fit?" doorway access checker.
 *
 * Multimodal Prompt API (Chrome built-in AI). Customer uploads a photo of
 * their access route (front door, hallway, stair landing). Gemini Nano
 * estimates clearance dimensions and we cross-check against the catalogue
 * to surface fitting baths. All inference runs on the customer's device.
 *
 * Requires: Chrome 139+ with the Prompt API origin trial token in <meta>
 * OR chrome://flags/#prompt-api-for-gemini-nano-multimodal-input enabled.
 * Silent on iOS Safari and any browser without LanguageModel.
 */
(function () {
  'use strict';

  // Bath catalogue with critical access dimensions in mm.
  // Width × Length × Height (depth). Edge-on transport reduces effective width to height.
  // Sourced from product titles + product specs; covers the most-shipped lines.
  var BATHS = [
    { handle: '1500-x-700-bathtub-omnitub', title: '1500 × 700 Bath',     width: 700, length: 1500, height: 600 },
    { handle: '1500-x-800-bathtub-omnitub', title: '1500 × 800 Bath',     width: 800, length: 1500, height: 600 },
    { handle: '1600-x-700-bathtub-omnitub', title: '1600 × 700 Bath',     width: 700, length: 1600, height: 600 },
    { handle: '1600-x-800-bathtub-omnitub', title: '1600 × 800 Bath',     width: 800, length: 1600, height: 600 },
    { handle: '1700-x-700-bathtub-omnitub', title: '1700 × 700 Bath',     width: 700, length: 1700, height: 600 },
    { handle: '1700-x-800-bathtub-omnitub', title: '1700 × 800 Bath',     width: 800, length: 1700, height: 600 },
    { handle: '1800-x-800-bathtub-omnitub', title: '1800 × 800 Bath',     width: 800, length: 1800, height: 600 },
    { handle: '1800-x-900-bathtub-omnitub', title: '1800 × 900 Bath',     width: 900, length: 1800, height: 600 },
    { handle: '1900-x-900-bathtub-omnitub', title: '1900 × 900 Bath',     width: 900, length: 1900, height: 600 },
    { handle: '2000-x-900-bathtub-omnitub', title: '2000 × 900 Bath',     width: 900, length: 2000, height: 600 }
  ];

  var SYSTEM_PROMPT =
    "You are a careful surveyor measuring bathroom access routes for delivering a heavy " +
    "freestanding bath. The user shows you a photograph of a doorway, hallway, stair " +
    "landing, or bathroom entrance. Your job: estimate the clearance dimensions in " +
    "centimetres using any visible reference scale (UK standard interior door = ~76 cm " +
    "wide × 198 cm tall; UK skirting ~12 cm; light switch ~120 cm above floor; brick " +
    "course ~21 cm; A4 paper ~21 × 30 cm; standard plug socket faceplate ~8.5 cm tall). " +
    "Be honest about uncertainty: if the photo lacks clear scale references, return " +
    "low confidence and explain. Note pinch points: tight 90° corners, low ceilings " +
    "below 198 cm, narrow staircases, banister obstructions, awkward shapes. " +
    "Never invent measurements you can't justify. If the photo doesn't show an access " +
    "route at all, say so in the notes and set confidence to low.";

  var SCHEMA = {
    type: 'object',
    required: ['narrowestWidthCm', 'lowestHeightCm', 'confidence', 'pinchPoints', 'notes'],
    properties: {
      narrowestWidthCm: {
        type: 'number',
        description: 'Narrowest clearance width along the route, in cm.'
      },
      lowestHeightCm: {
        type: 'number',
        description: 'Lowest clearance height along the route, in cm.'
      },
      confidence: {
        type: 'string',
        enum: ['low', 'medium', 'high']
      },
      pinchPoints: {
        type: 'array',
        items: { type: 'string' },
        description: 'Specific obstacles or tight spots noticed.'
      },
      notes: {
        type: 'string',
        description: 'Brief surveyor commentary on what you see.'
      }
    }
  };

  function isApiPresent() {
    return typeof self.LanguageModel !== 'undefined';
  }

  async function checkAvailability() {
    if (!isApiPresent()) return 'unavailable';
    try {
      return await self.LanguageModel.availability({
        expectedInputs: [{ type: 'image' }, { type: 'text', languages: ['en'] }],
        expectedOutputs: [{ type: 'text', languages: ['en'] }]
      });
    } catch (e) {
      return 'unavailable';
    }
  }

  // Verdict for one bath given clearance estimates.
  // Margin in mm — gives us a comfort buffer for grip + protective wrap.
  function verdictFor(bath, widthMm, heightMm, marginMm) {
    if (marginMm == null) marginMm = 50; // 5cm comfort margin
    var w = widthMm;
    var h = heightMm;

    // Edge-on (the usual delivery orientation): bath stood on its long edge.
    // Effective width = bath.height (depth), effective length = bath.length.
    var edgeFitsComfortably = bath.height <= (w - marginMm) && bath.length <= (h - marginMm);
    var edgeFitsTight       = bath.height <= w               && bath.length <= h;

    // Flat (carried horizontally): width = bath.width, length = bath.length.
    // Rare for tubs but useful where the route has lots of space.
    var flatFitsComfortably = bath.width  <= (w - marginMm) && bath.length <= (h - marginMm);

    if (edgeFitsComfortably) return 'comfortably';
    if (edgeFitsTight)        return 'tight';
    if (flatFitsComfortably)  return 'flat-only';
    return 'no';
  }

  function verdictLabel(v) {
    return ({
      'comfortably': 'Fits comfortably',
      'tight':       'Tight — measure twice',
      'flat-only':   'On its side only',
      'no':          'Won’t fit through this route'
    })[v] || '';
  }

  function verdictTone(v) {
    return ({ comfortably: 'good', tight: 'warn', 'flat-only': 'warn', no: 'no' })[v] || 'warn';
  }

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function buildUnsupportedNotice() {
    var d = el('div', 'omnitub-fit__unsupported');
    d.innerHTML =
      '<h3>This tool needs Chrome on a desktop computer.</h3>' +
      '<p>The doorway checker runs entirely on your device using Chrome’s built-in AI — ' +
      'so it doesn’t work in Safari, on iPhone, or in older browsers. ' +
      '<a class="omn-wa-link" href="https://wa.me/441934751200">WhatsApp us a photo</a> instead and we’ll measure it for you.</p>';
    return d;
  }

  function buildResultUi(estimate, verdicts) {
    var conf = estimate.confidence || 'medium';
    var widthCm = Math.round(estimate.narrowestWidthCm || 0);
    var heightCm = Math.round(estimate.lowestHeightCm || 0);

    var wrap = el('div', 'omnitub-fit__result');

    var head = el('div', 'omnitub-fit__result-head');
    head.innerHTML =
      '<div class="omnitub-fit__estimate">' +
        '<span class="omnitub-fit__estimate-label">Estimated narrowest clearance</span>' +
        '<strong class="omnitub-fit__estimate-value">' + widthCm + ' × ' + heightCm + ' cm</strong>' +
        '<span class="omnitub-fit__estimate-conf" data-conf="' + conf + '">Confidence: ' + conf + '</span>' +
      '</div>';
    wrap.appendChild(head);

    if (estimate.notes) {
      var notes = el('p', 'omnitub-fit__notes', estimate.notes);
      wrap.appendChild(notes);
    }

    if (estimate.pinchPoints && estimate.pinchPoints.length) {
      var pp = el('div', 'omnitub-fit__pinch');
      pp.innerHTML = '<h4>Watch out for</h4><ul>' +
        estimate.pinchPoints.map(function (p) { return '<li>' + p + '</li>'; }).join('') +
        '</ul>';
      wrap.appendChild(pp);
    }

    var list = el('div', 'omnitub-fit__verdicts');
    list.innerHTML = '<h4>Which baths can pass through</h4>';
    var grid = el('div', 'omnitub-fit__grid');
    verdicts.forEach(function (v) {
      var card = el('a', 'omnitub-fit__card omnitub-fit__card--' + verdictTone(v.verdict));
      card.href = '/products/' + v.bath.handle;
      card.target = '_blank';
      card.rel = 'noopener';
      card.innerHTML =
        '<span class="omnitub-fit__card-dot" aria-hidden="true"></span>' +
        '<span class="omnitub-fit__card-title">' + v.bath.title + '</span>' +
        '<span class="omnitub-fit__card-status">' + verdictLabel(v.verdict) + '</span>';
      grid.appendChild(card);
    });
    list.appendChild(grid);
    wrap.appendChild(list);

    var disclaimer = el('p', 'omnitub-fit__disclaimer');
    disclaimer.innerHTML =
      'This is an estimate from your photograph — confidence depends on the reference points visible. ' +
      'Before ordering, please tape-measure the narrowest doorway and the route into your bathroom, ' +
      'or <a class="omn-wa-link" href="https://wa.me/441934751200">WhatsApp the photo to our team</a> and we’ll check with you.';
    wrap.appendChild(disclaimer);

    return wrap;
  }

  function buildBlobFromInput(input) {
    return new Promise(function (resolve, reject) {
      var file = input.files && input.files[0];
      if (!file) { reject(new Error('No file selected')); return; }
      // Accept images only
      if (!/^image\//.test(file.type)) {
        reject(new Error('Please pick an image (JPG, PNG, HEIC).'));
        return;
      }
      resolve(file);
    });
  }

  function previewImage(blob, container) {
    container.innerHTML = '';
    var img = el('img', 'omnitub-fit__preview-img');
    img.src = URL.createObjectURL(blob);
    img.alt = 'Your access route photograph';
    container.appendChild(img);
  }

  async function init() {
    var root = document.querySelector('[data-omnitub-fit]');
    if (!root) return;

    var supported = isApiPresent();
    var availability = supported ? await checkAvailability() : 'unavailable';

    var statusEl = root.querySelector('[data-omnitub-fit-status]');
    var fileInput = root.querySelector('[data-omnitub-fit-file]');
    var preview = root.querySelector('[data-omnitub-fit-preview]');
    var checkBtn = root.querySelector('[data-omnitub-fit-check]');
    var resetBtn = root.querySelector('[data-omnitub-fit-reset]');
    var resultEl = root.querySelector('[data-omnitub-fit-result]');

    if (!supported || availability === 'unavailable') {
      resultEl.innerHTML = '';
      resultEl.appendChild(buildUnsupportedNotice());
      // Hide the form on unsupported browsers — no point showing it.
      var form = root.querySelector('.omnitub-fit__form');
      if (form) form.hidden = true;
      return;
    }

    // Surface the model state up-front
    if (availability === 'after-download' || availability === 'downloadable') {
      statusEl.textContent = 'On first use we’ll download a small AI model to your device (~2 GB). It’s cached after that.';
    } else if (availability === 'downloading') {
      statusEl.textContent = 'AI model is preparing — try again in a moment.';
    }

    var currentBlob = null;

    fileInput.addEventListener('change', async function () {
      try {
        currentBlob = await buildBlobFromInput(fileInput);
        previewImage(currentBlob, preview);
        checkBtn.disabled = false;
        statusEl.textContent = 'Ready — tap Check fit when you’re happy with the photo.';
        resultEl.innerHTML = '';
      } catch (e) {
        statusEl.textContent = e.message;
        checkBtn.disabled = true;
      }
    });

    resetBtn.addEventListener('click', function () {
      fileInput.value = '';
      currentBlob = null;
      preview.innerHTML = '';
      resultEl.innerHTML = '';
      checkBtn.disabled = true;
      statusEl.textContent = '';
    });

    checkBtn.addEventListener('click', async function () {
      if (!currentBlob) return;
      checkBtn.disabled = true;
      checkBtn.classList.add('omnitub-fit__btn--working');
      statusEl.textContent = 'Reading your photo on your device — usually 5–15 seconds.';
      resultEl.innerHTML = '';

      try {
        var session = await self.LanguageModel.create({
          expectedInputs: [{ type: 'image' }, { type: 'text', languages: ['en'] }],
          expectedOutputs: [{ type: 'text', languages: ['en'] }],
          initialPrompts: [{ role: 'system', content: SYSTEM_PROMPT }],
          monitor: function (m) {
            m.addEventListener('downloadprogress', function (e) {
              statusEl.textContent = 'Downloading AI model to your device — ' + Math.round(e.loaded * 100) + '%';
            });
          }
        });

        var rawResponse = await session.prompt([
          {
            role: 'user',
            content: [
              { type: 'image', value: currentBlob },
              { type: 'text', value: 'Survey this access route for delivering a freestanding bathtub. Return only JSON matching the schema.' }
            ]
          }
        ], { responseConstraint: SCHEMA });

        var estimate;
        try {
          estimate = typeof rawResponse === 'string' ? JSON.parse(rawResponse) : rawResponse;
        } catch (e) {
          throw new Error('AI returned malformed response.');
        }

        var widthMm = (estimate.narrowestWidthCm || 0) * 10;
        var heightMm = (estimate.lowestHeightCm || 0) * 10;
        var verdicts = BATHS.map(function (bath) {
          return { bath: bath, verdict: verdictFor(bath, widthMm, heightMm) };
        });
        // Order: comfortably > tight > flat-only > no
        var order = { comfortably: 0, tight: 1, 'flat-only': 2, no: 3 };
        verdicts.sort(function (a, b) {
          var d = order[a.verdict] - order[b.verdict];
          return d !== 0 ? d : a.bath.length - b.bath.length;
        });

        resultEl.appendChild(buildResultUi(estimate, verdicts));
        statusEl.textContent = '';
      } catch (e) {
        console.warn('[omnitub-fit] ' + e.message);
        var msg = el('div', 'omnitub-fit__error');
        msg.innerHTML =
          '<p>We couldn’t read this photo on your device. ' +
          '<a class="omn-wa-link" href="https://wa.me/441934751200">WhatsApp it to our team</a> and we’ll measure it for you.</p>';
        resultEl.appendChild(msg);
        statusEl.textContent = '';
      } finally {
        checkBtn.disabled = false;
        checkBtn.classList.remove('omnitub-fit__btn--working');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
